import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, runTransaction } from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import { buildAffiliateTurnoverUpdate } from "../../lib/affiliateBonus";
import { getInhouseGame, inhouseGames } from "./inhouseGames";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function sample(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  const next = [...list];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function formatCurrency(value) {
  return `${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USDT`;
}

const wheelRiskMap = {
  low: [0.2, 0.5, 0.8, 1, 1.2, 1.5, 2, 3],
  medium: [0, 0.3, 0.5, 1, 1.8, 3, 5, 10],
  high: [0, 0, 0.2, 0.5, 1.5, 5, 10, 25],
};

const luckyWheelSegments = {
  low: [
    { label: "0.20x", multiplier: 0.2, color: "#f59e0b" },
    { label: "0.50x", multiplier: 0.5, color: "#ef4444" },
    { label: "0.80x", multiplier: 0.8, color: "#22c55e" },
    { label: "1.00x", multiplier: 1, color: "#2563eb" },
    { label: "1.20x", multiplier: 1.2, color: "#9333ea" },
    { label: "1.50x", multiplier: 1.5, color: "#f97316" },
    { label: "2.00x", multiplier: 2, color: "#10b981" },
    { label: "3.00x", multiplier: 3, color: "#ec4899" },
  ],
  medium: [
    { label: "0.00x", multiplier: 0, color: "#ef4444" },
    { label: "0.30x", multiplier: 0.3, color: "#f59e0b" },
    { label: "0.50x", multiplier: 0.5, color: "#2563eb" },
    { label: "1.00x", multiplier: 1, color: "#22c55e" },
    { label: "1.80x", multiplier: 1.8, color: "#9333ea" },
    { label: "3.00x", multiplier: 3, color: "#f97316" },
    { label: "5.00x", multiplier: 5, color: "#10b981" },
    { label: "10.00x", multiplier: 10, color: "#ec4899" },
  ],
  high: [
    { label: "0.00x", multiplier: 0, color: "#ef4444" },
    { label: "0.00x", multiplier: 0, color: "#dc2626" },
    { label: "0.20x", multiplier: 0.2, color: "#f59e0b" },
    { label: "0.50x", multiplier: 0.5, color: "#2563eb" },
    { label: "1.50x", multiplier: 1.5, color: "#22c55e" },
    { label: "5.00x", multiplier: 5, color: "#9333ea" },
    { label: "10.00x", multiplier: 10, color: "#10b981" },
    { label: "25.00x", multiplier: 25, color: "#ec4899" },
  ],
};

const kenoPayouts = [0, 0, 0, 1.4, 2.25, 4.5, 8, 17, 50, 80, 100];

function getInitialState(slug) {
  switch (slug) {
    case "limbo":
      return { target: 1.98 };
    case "dice-duel":
      return { direction: "over", line: 50 };
    case "coin-toss":
      return { side: "heads" };
    case "lucky-wheel":
      return { risk: "medium" };
    case "keno":
      return { picks: [5, 12, 14, 17, 18, 24, 28, 32, 38, 39] };
    case "mines":
      return { picks: [1, 7, 13], mineCount: 5 };
    case "hi-lo":
      return { guess: "higher", startCard: 7 };
    case "plinko-lite":
      return { risk: "medium" };
    case "roulette-mini":
      return { bet: "red" };
    case "slots-sprint":
      return {};
    case "dragon-tower":
      return { floors: 3 };
    case "scratch-gold":
      return {};
    default:
      return {};
  }
}

function simulateGame(game, state, stake) {
  const safeStake = Number(stake || 0);

  switch (game.slug) {
    case "limbo": {
      const target = clamp(Number(state.target || 1.98), 1.1, 25);
      const crashAt = clamp(
        Number((1 / Math.max(Math.random(), 0.04)).toFixed(2)),
        1.01,
        25,
      );
      const won = crashAt >= target;
      return {
        title: won ? "Target cleared" : "Target missed",
        detail: `Round flew to ${crashAt.toFixed(2)}x against your ${target.toFixed(2)}x target.`,
        payout: won ? safeStake * target * 0.96 : 0,
        chips: [`Crash ${crashAt.toFixed(2)}x`, `Target ${target.toFixed(2)}x`],
        meta: { target, crashAt },
      };
    }
    case "dice-duel": {
      const line = clamp(Number(state.line || 50), 5, 95);
      const roll = Math.floor(Math.random() * 100);
      const direction = state.direction === "under" ? "under" : "over";
      const winChance = direction === "over" ? (100 - line) / 100 : line / 100;
      const won = direction === "over" ? roll > line : roll < line;
      const payoutMultiplier = 0.97 / Math.max(winChance, 0.05);
      return {
        title: won ? "Line beaten" : "House takes it",
        detail: `Roll landed on ${roll} with an ${direction} ${line} call.`,
        payout: won ? safeStake * payoutMultiplier : 0,
        chips: [`Roll ${roll}`, `${direction} ${line}`],
        meta: { roll, line, direction, payoutMultiplier, winChance },
      };
    }
    case "coin-toss": {
      const flip = Math.random() < 0.5 ? "heads" : "tails";
      const won = flip === state.side;
      return {
        title: won ? "Correct call" : "Wrong side",
        detail: `The coin landed on ${flip}.`,
        payout: won ? safeStake * 1.96 : 0,
        chips: [`Pick ${state.side}`, `Result ${flip}`],
        meta: { flip, side: state.side },
      };
    }
    case "lucky-wheel": {
      const risk = wheelRiskMap[state.risk] ? state.risk : "medium";
      const segments = luckyWheelSegments[risk];
      const segmentIndex = Math.floor(Math.random() * segments.length);
      const hit = segments[segmentIndex];
      return {
        title: hit.multiplier > 0 ? "Wheel paid out" : "Wheel blanked",
        detail: `The ${risk} wheel stopped on ${hit.label}.`,
        payout: safeStake * hit.multiplier,
        chips: [`Risk ${risk}`, `Hit ${hit.label}`],
        meta: { risk, segmentIndex, hit },
      };
    }
    case "keno": {
      const picks = shuffle(state.picks || [])
        .slice(0, 10)
        .sort((left, right) => left - right);
      const draw = shuffle(Array.from({ length: 40 }, (_, index) => index + 1))
        .slice(0, 10)
        .sort((left, right) => left - right);
      const matches = picks.filter((value) => draw.includes(value)).length;
      const multiplier = kenoPayouts[matches] || 0;
      return {
        title: matches ? "Numbers matched" : "Cold board",
        detail: `You matched ${matches} of ${picks.length} numbers.`,
        payout: safeStake * multiplier,
        chips: [`Hits ${matches}`, `Draw ${draw.slice(0, 4).join(", ")}...`],
        meta: { picks, draw, matches, multiplier },
      };
    }
    case "mines": {
      const chosen = shuffle(state.picks || [])
        .slice(0, 3)
        .sort((left, right) => left - right);
      const mineCount = clamp(Number(state.mineCount || 5), 3, 8);
      const mineTiles = shuffle(
        Array.from({ length: 25 }, (_, index) => index + 1),
      ).slice(0, mineCount);
      const hitMine = chosen.some((value) => mineTiles.includes(value));
      const safeCount = chosen.length;
      const multiplier = hitMine ? 0 : [0, 1.35, 2.15, 3.4][safeCount] || 0;
      return {
        title: hitMine ? "Mine exploded" : "Clean path",
        detail: `Hidden mines were on ${mineTiles.sort((a, b) => a - b).join(", ")}.`,
        payout: safeStake * multiplier,
        chips: [`Safe picks ${chosen.join(",")}`, `${mineCount} mines live`],
      };
    }
    case "hi-lo": {
      const currentCard = clamp(Number(state.startCard || 7), 2, 10);
      const nextCard = Math.ceil(Math.random() * 13);
      const won = state.guess === "higher" ? nextCard > currentCard : nextCard < currentCard;
      const push = nextCard === currentCard;
      return {
        title: push ? "Push" : won ? "Read it right" : "Bad read",
        detail: `Current card ${currentCard}, next card ${nextCard}.`,
        payout: push ? safeStake : won ? safeStake * 1.92 : 0,
        chips: [`Start ${currentCard}`, `Next ${nextCard}`],
      };
    }
    case "plinko-lite": {
      const riskTable = {
        low: [0.5, 0.8, 1, 1.4, 2],
        medium: [0.2, 0.6, 1.2, 2, 4],
        high: [0, 0.4, 1.6, 3, 6],
      };
      const risk = riskTable[state.risk] ? state.risk : "medium";
      const multiplier = sample(riskTable[risk]);
      return {
        title: multiplier >= 1 ? "Chip found the pocket" : "Chip slid low",
        detail: `The ${risk} drop landed at ${multiplier.toFixed(2)}x.`,
        payout: safeStake * multiplier,
        chips: [`Risk ${risk}`, `Pocket ${multiplier.toFixed(2)}x`],
      };
    }
    case "roulette-mini": {
      const number = Math.floor(Math.random() * 37);
      const redNumbers = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
      const color = number === 0 ? "green" : redNumbers.has(number) ? "red" : "black";
      const parity = number === 0 ? "zero" : number % 2 === 0 ? "even" : "odd";
      const won = state.bet === color || state.bet === parity;
      return {
        title: won ? "Wheel matched" : "Wheel missed",
        detail: `Ball stopped on ${number} (${color}).`,
        payout: won ? safeStake * 1.95 : 0,
        chips: [`Bet ${state.bet}`, `Spin ${number}`],
      };
    }
    case "slots-sprint": {
      const symbols = ["BAR", "GOLD", "STAR", "SEVEN", "GEM"];
      const reels = [sample(symbols), sample(symbols), sample(symbols)];
      const counts = reels.reduce((map, symbol) => {
        map[symbol] = (map[symbol] || 0) + 1;
        return map;
      }, {});
      const topCount = Math.max(...Object.values(counts));
      let multiplier = 0;
      if (topCount === 3) {
        multiplier = reels[0] === "SEVEN" ? 10 : 5;
      } else if (topCount === 2) {
        multiplier = 1.5;
      }
      return {
        title: multiplier ? "Reels connected" : "No connect",
        detail: `Spin result: ${reels.join(" | ")}.`,
        payout: safeStake * multiplier,
        chips: reels,
      };
    }
    case "dragon-tower": {
      const floors = clamp(Number(state.floors || 3), 2, 5);
      const survive = Math.random() < [0, 0, 0.78, 0.62, 0.46, 0.32][floors];
      const multiplier = [0, 0, 1.55, 2.25, 3.4, 5][floors];
      return {
        title: survive ? "Tower cleared" : "Tower collapsed",
        detail: `You called a ${floors}-floor climb.`,
        payout: survive ? safeStake * multiplier : 0,
        chips: [`Floors ${floors}`, survive ? "Safe run" : "Trap hit"],
      };
    }
    case "scratch-gold": {
      const symbols = ["gold", "crown", "vault", "ruby"];
      const cards = [sample(symbols), sample(symbols), sample(symbols)];
      const counts = cards.reduce((map, symbol) => {
        map[symbol] = (map[symbol] || 0) + 1;
        return map;
      }, {});
      const topCount = Math.max(...Object.values(counts));
      const multiplier = topCount === 3 ? 6 : topCount === 2 ? 1.8 : 0;
      return {
        title: multiplier ? "Scratch hit" : "No pair",
        detail: `You uncovered ${cards.join(", ")}.`,
        payout: safeStake * multiplier,
        chips: cards,
      };
    }
    default:
      return {
        title: "Round void",
        detail: "This game could not be simulated.",
        payout: safeStake,
        chips: ["Refund"],
      };
  }
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function SegmentedButton({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[14px] px-3 py-2 text-sm font-semibold transition ${
        active
          ? "bg-emerald-500 text-[#07120d]"
          : "bg-[#232a35] text-slate-300 hover:bg-[#2a313d]"
      }`}
    >
      {label}
    </button>
  );
}

function SurfaceStat({ label, value, valueClassName = "text-white" }) {
  return (
    <div className="rounded-[18px] bg-[#1a2029] px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
        {label}
      </div>
      <div className={`mt-2 text-lg font-black ${valueClassName}`}>{value}</div>
    </div>
  );
}

function renderControls(game, state, setState) {
  switch (game.slug) {
    case "limbo":
      return (
        <div className="space-y-3">
          <ControlField label="Payout">
            <input
              type="number"
              min="1.1"
              max="25"
              step="0.01"
              value={state.target}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  target: event.target.value,
                }))
              }
              className="w-full rounded-[16px] bg-[#1b212b] px-4 py-3 text-white outline-none"
            />
          </ControlField>
          <SurfaceStat
            label="Win Chance"
            value={`${(100 / Math.max(Number(state.target || 1.98), 1.01)).toFixed(4)}%`}
            valueClassName="text-emerald-300"
          />
        </div>
      );
    case "dice-duel": {
      const chance =
        (state.direction === "over"
          ? 100 - Number(state.line || 50)
          : Number(state.line || 50)) / 100;
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["over", "under"].map((direction) => (
              <SegmentedButton
                key={direction}
                active={state.direction === direction}
                label={direction.toUpperCase()}
                onClick={() =>
                  setState((current) => ({
                    ...current,
                    direction,
                  }))
                }
              />
            ))}
          </div>
          <ControlField label="Target">
            <input
              type="range"
              min="5"
              max="95"
              value={state.line}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  line: event.target.value,
                }))
              }
              className="w-full"
            />
          </ControlField>
          <div className="grid grid-cols-2 gap-3">
            <SurfaceStat label="Roll Over" value={String(state.line)} />
            <SurfaceStat
              label="Payout"
              value={`${(0.97 / Math.max(chance, 0.05)).toFixed(2)}x`}
              valueClassName="text-emerald-300"
            />
          </div>
        </div>
      );
    }
    case "coin-toss":
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["heads", "tails"].map((side) => (
              <SegmentedButton
                key={side}
                active={state.side === side}
                label={side}
                onClick={() =>
                  setState((current) => ({
                    ...current,
                    side,
                  }))
                }
              />
            ))}
          </div>
          <SurfaceStat label="Payout" value="1.96x" valueClassName="text-emerald-300" />
        </div>
      );
    case "lucky-wheel":
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {["low", "medium", "high"].map((risk) => (
              <SegmentedButton
                key={risk}
                active={state.risk === risk}
                label={risk}
                onClick={() =>
                  setState((current) => ({
                    ...current,
                    risk,
                  }))
                }
              />
            ))}
          </div>
          <SurfaceStat
            label="Risk Mode"
            value={state.risk}
            valueClassName="capitalize text-amber-300"
          />
        </div>
      );
    case "keno":
      return (
        <div className="space-y-4">
          <SurfaceStat
            label="Selected"
            value={`${state.picks.length} Numbers`}
            valueClassName="text-violet-300"
          />
          <div className="text-sm leading-7 text-slate-400">
            Pick up to 10 numbers from the board. Payout climbs sharply on 6+
            hits.
          </div>
        </div>
      );
    default:
      return (
        <div className="text-sm leading-7 text-slate-400">
          This game runs on an instant result. Set your amount and fire a round
          when you are ready.
        </div>
      );
  }
}

function ControlField({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-xs uppercase tracking-[0.24em] text-slate-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function getLimboWinChance(target) {
  const safeTarget = clamp(Number(target || 1.98), 1.1, 25);
  return clamp(98 / safeTarget, 0.01, 98);
}

function changeLimboTarget(value, delta) {
  const nextValue = clamp(
    Number((Number(value || 1.98) + delta).toFixed(2)),
    1.1,
    25,
  );
  return nextValue.toFixed(2);
}

export default function InHouseGamePage({ user, balance = 0 }) {
  const navigate = useNavigate();
  const { gameSlug } = useParams();
  const game = useMemo(() => getInhouseGame(gameSlug), [gameSlug]);
  const [stake, setStake] = useState(10);
  const [state, setState] = useState(() => getInitialState(gameSlug));
  const [placing, setPlacing] = useState(false);
  const [lastRound, setLastRound] = useState(null);
  const [visualState, setVisualState] = useState({
    limboValue: 1,
    limboAnimating: false,
    diceValue: 50,
    diceRolling: false,
    coinFace: "heads",
    coinFlipping: false,
    wheelRotation: 0,
    wheelSpinning: false,
    kenoReveal: [],
  });
  const animationRef = useRef(null);

  useEffect(() => {
    setState(getInitialState(gameSlug));
    setLastRound(null);
    setVisualState({
      limboValue: 1,
      limboAnimating: false,
      diceValue: 50,
      diceRolling: false,
      coinFace: "heads",
      coinFlipping: false,
      wheelRotation: 0,
      wheelSpinning: false,
      kenoReveal: [],
    });
  }, [gameSlug]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        window.clearInterval(animationRef.current);
      }
    };
  }, []);

  const relatedGames = useMemo(
    () => inhouseGames.filter((entry) => entry.slug !== gameSlug).slice(0, 4),
    [gameSlug],
  );

  if (!game) {
    return (
      <div className="min-h-screen bg-[#171b22] text-white md:mt-14">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <div className="rounded-[32px] bg-[#1e232c] p-8 text-center">
            <h1 className="text-3xl font-black">Game not found</h1>
            <button
              type="button"
              onClick={() => navigate("/casino")}
              className="mt-6 rounded-full bg-emerald-500 px-5 py-3 font-bold text-[#06120d]"
            >
              Back to casino
            </button>
          </div>
        </div>
      </div>
    );
  }

  const playRound = async () => {
    const numericStake = Number(stake || 0);
    if (!user?.uid) {
      sessionStorage.setItem("postLoginRedirect", `/casino/${game.slug}`);
      navigate("/login");
      return;
    }
    if (numericStake < 1) {
      toast.error("Minimum stake is 1 USDT.");
      return;
    }
    if (numericStake > Number(balance || 0)) {
      toast.error("Insufficient balance.");
      return;
    }
    if (game.slug === "keno" && (state.picks || []).length < 1) {
      toast.error("Pick at least 1 number for Mini Keno.");
      return;
    }
    if (game.slug === "mines" && (state.picks || []).length !== 3) {
      toast.error("Pick exactly 3 tiles for Mines Rush.");
      return;
    }

    const round = simulateGame(game, state, numericStake);
    const payout = Number(round.payout || 0);
    const roundRef = doc(collection(db, "casino_rounds"));

    setPlacing(true);
    try {
      if (animationRef.current) {
        window.clearInterval(animationRef.current);
      }

      if (game.slug === "limbo") {
        const crashAt = Number(round.meta?.crashAt || 1);
        const duration = 1300;
        const startedAt = Date.now();
        setVisualState((current) => ({
          ...current,
          limboAnimating: true,
          limboValue: 1,
        }));
        animationRef.current = window.setInterval(() => {
          const elapsed = Date.now() - startedAt;
          const progress = Math.min(1, elapsed / duration);
          const nextValue = 1 + (crashAt - 1) * progress;
          setVisualState((current) => ({ ...current, limboValue: nextValue }));
          if (progress >= 1) {
            window.clearInterval(animationRef.current);
          }
        }, 26);
        await delay(duration + 120);
      }

      if (game.slug === "dice-duel") {
        const duration = 1000;
        const startedAt = Date.now();
        setVisualState((current) => ({ ...current, diceRolling: true }));
        animationRef.current = window.setInterval(() => {
          setVisualState((current) => ({
            ...current,
            diceValue: Math.floor(Math.random() * 100),
          }));
          if (Date.now() - startedAt >= duration) {
            window.clearInterval(animationRef.current);
          }
        }, 80);
        await delay(duration);
        setVisualState((current) => ({
          ...current,
          diceRolling: false,
          diceValue: Number(round.meta?.roll || current.diceValue),
        }));
      }

      if (game.slug === "coin-toss") {
        const duration = 1100;
        const startedAt = Date.now();
        setVisualState((current) => ({ ...current, coinFlipping: true }));
        animationRef.current = window.setInterval(() => {
          setVisualState((current) => ({
            ...current,
            coinFace: Math.random() > 0.5 ? "heads" : "tails",
          }));
          if (Date.now() - startedAt >= duration) {
            window.clearInterval(animationRef.current);
          }
        }, 120);
        await delay(duration);
        setVisualState((current) => ({
          ...current,
          coinFlipping: false,
          coinFace: round.meta?.flip || current.coinFace,
        }));
      }

      if (game.slug === "lucky-wheel") {
        const segmentIndex = Number(round.meta?.segmentIndex || 0);
        const segments = luckyWheelSegments[round.meta?.risk || "medium"];
        const degreesPerSegment = 360 / segments.length;
        setVisualState((current) => ({
          ...current,
          wheelSpinning: true,
          wheelRotation:
            current.wheelRotation +
            1440 +
            (360 - segmentIndex * degreesPerSegment - degreesPerSegment / 2),
        }));
        await delay(2200);
        setVisualState((current) => ({
          ...current,
          wheelSpinning: false,
        }));
      }

      if (game.slug === "keno") {
        const draw = round.meta?.draw || [];
        setVisualState((current) => ({ ...current, kenoReveal: [] }));
        for (let index = 0; index < draw.length; index += 1) {
          // Reveal one draw result at a time to create a board animation.
          // eslint-disable-next-line no-await-in-loop
          await delay(120);
          setVisualState((current) => ({
            ...current,
            kenoReveal: [...draw.slice(0, index + 1)],
          }));
        }
      }

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) throw new Error("User not found.");
        const currentBalance = Number(userSnap.data().balance || 0);
        if (currentBalance < numericStake) throw new Error("Insufficient balance.");
        const userData = userSnap.data() || {};
        const currentTurnover = Number(userData.turnoverCompleted || 0);
        const nextBalance = +(currentBalance - numericStake + payout).toFixed(2);
        const affiliateUpdate = buildAffiliateTurnoverUpdate(userData, numericStake);

        transaction.update(userRef, {
          balance: nextBalance,
          turnoverCompleted: +(currentTurnover + numericStake).toFixed(2),
          lastPlayedGame: game.slug,
          ...affiliateUpdate.fields,
        });
        transaction.set(roundRef, {
          userId: user.uid,
          gameSlug: game.slug,
          gameName: game.name,
          stake: numericStake,
          payout: +payout.toFixed(2),
          won: payout > numericStake,
          resultTitle: round.title,
          resultDetail: round.detail,
          createdAt: new Date(),
        });
      });

      setLastRound(round);
      toast.success(
        payout > numericStake ? "Round won and wallet updated." : "Round settled.",
      );
    } catch (error) {
      toast.error(error?.message || "Failed to play round.");
    } finally {
      setVisualState((current) => ({
        ...current,
        limboAnimating: false,
        diceRolling: false,
        coinFlipping: false,
        wheelSpinning: false,
      }));
      setPlacing(false);
    }
  };

  if (game.slug === "limbo") {
    return (
      <LimboGameScreen
        game={game}
        stake={stake}
        setStake={setStake}
        state={state}
        setState={setState}
        balance={balance}
        lastRound={lastRound}
        visualState={visualState}
        placing={placing}
        playRound={playRound}
        navigate={navigate}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#171b22] text-white md:mt-16">
      <div className="mx-auto max-w-[1500px] px-3 py-5 sm:px-5 lg:px-6">
        <div className="grid gap-4 xl:grid-cols-[300px_1fr]">
          <aside className="order-2 space-y-4 xl:sticky xl:top-24 xl:order-1 xl:self-start">
            <div className="rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
              <div className="grid grid-cols-2 gap-2 rounded-[18px] bg-[#171b22] p-1">
                <div className="rounded-[14px] bg-[#232a35] px-3 py-2 text-center text-sm font-bold text-white">
                  Manual
                </div>
                <div className="rounded-[14px] px-3 py-2 text-center text-sm font-semibold text-slate-500">
                  Auto
                </div>
              </div>

              <div className="mt-4 space-y-4">
                <ControlField label="Amount">
                  <input
                    type="number"
                    min="1"
                    value={stake}
                    onChange={(event) => setStake(event.target.value)}
                    className="w-full rounded-[16px] bg-[#171d26] px-4 py-3 text-white outline-none"
                  />
                </ControlField>

                <div className="grid grid-cols-4 gap-2">
                  {[0.5, 2, 10, 100].map((value, index) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setStake((current) => {
                          const base = Number(current || 0);
                          if (index === 0) return Math.max(1, base / 2);
                          if (index === 1) return base * 2 || 2;
                          return value;
                        })
                      }
                      className="rounded-[14px] bg-[#232a35] px-2 py-2 text-xs font-semibold text-slate-300 transition hover:bg-[#2a313d]"
                    >
                      {index === 0 ? "/2" : index === 1 ? "x2" : value}
                    </button>
                  ))}
                </div>

                {renderControls(game, state, setState)}

                <button
                  type="button"
                  onClick={playRound}
                  disabled={placing}
                  className="w-full rounded-[18px] bg-[#31c96a] px-5 py-4 text-base font-black text-[#07110d] transition hover:bg-[#48d779] disabled:bg-[#2b313c] disabled:text-slate-500"
                >
                  {placing ? "Running..." : "Bet"}
                </button>
              </div>
            </div>

            <div className="rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
              <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                Wallet
              </div>
              <div className="mt-3 text-3xl font-black text-white">
                {formatCurrency(balance)}
              </div>
              <div className="mt-4 grid gap-3">
                <SurfaceStat label="Game" value={game.name} />
                <SurfaceStat label="Category" value={game.category} />
              </div>
            </div>

            <div className="rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
              <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                More Originals
              </div>
              <div className="mt-4 space-y-3">
                {relatedGames.map((entry) => (
                  <button
                    key={entry.slug}
                    type="button"
                    onClick={() => navigate(`/casino/${entry.slug}`)}
                    className="w-full rounded-[18px] bg-[#171d26] px-4 py-4 text-left transition hover:bg-[#202731]"
                  >
                    <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      {entry.category}
                    </div>
                    <div className="mt-2 text-base font-black text-white">
                      {entry.name}
                    </div>
                    <div className="mt-1 text-sm text-slate-400">
                      {entry.edge}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section className="order-1 rounded-[30px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)] sm:p-5 xl:order-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                  Originals
                </div>
                <h1 className="mt-2 text-3xl font-black text-white sm:text-4xl">
                  {game.name}
                </h1>
              </div>
              <button
                type="button"
                onClick={() => navigate("/casino")}
                className="rounded-[16px] bg-[#2a3039] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#313844]"
              >
                Back to lobby
              </button>
            </div>

            <div className="mt-5">
              <GameStage
                game={game}
                state={state}
                setState={setState}
                lastRound={lastRound}
                visualState={visualState}
              />
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_0.42fr]">
              <div className="rounded-[26px] bg-[#181d25] p-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
                  Game Info
                </div>
                <p className="mt-4 text-sm leading-8 text-slate-300">
                  {game.description}
                </p>
              </div>
              <div className="rounded-[26px] bg-[#181d25] p-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
                  Last Round
                </div>
                {lastRound ? (
                  <>
                    <div className="mt-3 text-2xl font-black text-white">
                      {lastRound.title}
                    </div>
                    <p className="mt-3 text-sm leading-7 text-slate-400">
                      {lastRound.detail}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {lastRound.chips.map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full bg-[#232a35] px-3 py-2 text-xs font-semibold text-slate-200"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                    <div
                      className={`mt-5 text-lg font-black ${
                        lastRound.payout > Number(stake)
                          ? "text-emerald-300"
                          : "text-white"
                      }`}
                    >
                      Payout: {formatCurrency(lastRound.payout)}
                    </div>
                  </>
                ) : (
                  <div className="mt-3 text-sm leading-7 text-slate-400">
                    Play a round to populate the result panel.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function LimboGameScreen({
  game,
  stake,
  setStake,
  state,
  setState,
  balance,
  lastRound,
  visualState,
  placing,
  playRound,
  navigate,
}) {
  const target = clamp(Number(state.target || 1.98), 1.1, 25);
  const currentValue = visualState.limboAnimating
    ? Number(visualState.limboValue || 1)
    : Number(lastRound?.meta?.crashAt || 1);
  const rocketLift = clamp((currentValue - 1) / 8, 0, 1);
  const winChance = getLimboWinChance(target);

  return (
    <div className="min-h-screen bg-[#171b22] text-white md:mt-16">
      <div className="mx-auto max-w-[1650px] px-2 py-4 sm:px-4 lg:px-5">
        <div className="overflow-hidden rounded-[30px] border border-white/5 bg-[#262b31] shadow-[0_30px_90px_rgba(0,0,0,0.4)]">
          <div className="grid min-h-[780px] lg:grid-cols-[335px_minmax(0,1fr)]">
            <aside className="order-2 flex flex-col border-b border-white/5 bg-[#2d3233] lg:order-1 lg:border-b-0 lg:border-r">
              <div className="grid grid-cols-3 border-b border-white/5 text-xl font-extrabold text-slate-500">
                {["Manual", "Auto", "Advanced"].map((label, index) => (
                  <div
                    key={label}
                    className={`relative px-6 py-8 text-center ${
                      index === 0 ? "text-slate-100" : ""
                    }`}
                  >
                    {label}
                    {label === "Advanced" ? (
                      <span className="absolute right-2 top-2 rounded bg-[#d4ce45] px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#25290f]">
                        New
                      </span>
                    ) : null}
                    {index === 0 ? (
                      <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#2ae278]" />
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="flex flex-1 flex-col px-6 py-8">
                <div className="flex items-center gap-2 text-[19px] font-black text-slate-100">
                  Amount
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#2ae278] text-[11px] text-[#2ae278]">
                    i
                  </span>
                </div>

                <div className="mt-4 rounded-[18px] border border-white/5 bg-[#2b3031] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-1.5">
                    <div className="flex items-center gap-3 rounded-[14px] bg-[#262b2c] px-4 py-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0f1318] text-lg font-black text-[#2ae278]">
                        b
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={stake}
                        onChange={(event) => setStake(event.target.value)}
                        className="w-full bg-transparent text-[20px] font-black text-white outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setStake((current) =>
                          Math.max(1, Number((Number(current || 0) / 2).toFixed(4))),
                        )
                      }
                      className="rounded-[14px] bg-[#4b4f4f] px-5 text-[18px] font-black text-white transition hover:bg-[#5a5f60]"
                    >
                      1/2
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setStake((current) =>
                          Number((Math.max(Number(current || 0), 0.0001) * 2).toFixed(4)),
                        )
                      }
                      className="rounded-[14px] bg-[#4b4f4f] px-5 text-[18px] font-black text-white transition hover:bg-[#5a5f60]"
                    >
                      2×
                    </button>
                    <div className="flex flex-col overflow-hidden rounded-[14px] bg-[#4b4f4f]">
                      <button
                        type="button"
                        onClick={() =>
                          setStake((current) =>
                            Number((Math.max(Number(current || 0), 0.0001) + 1).toFixed(4)),
                          )
                        }
                        className="flex h-full items-center justify-center px-4 py-2 text-lg font-black text-white transition hover:bg-[#5a5f60]"
                      >
                        ^
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setStake((current) =>
                            Math.max(1, Number((Math.max(Number(current || 0), 1) - 1).toFixed(4))),
                          )
                        }
                        className="flex h-full items-center justify-center border-t border-black/20 px-4 py-2 text-lg font-black text-white transition hover:bg-[#5a5f60]"
                      >
                        v
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-3">
                  {[1, 10, 100, 1000].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setStake(value)}
                      className="rounded-[14px] bg-[#393d3f] px-4 py-4 text-[17px] font-black text-slate-300 transition hover:bg-[#464b4d]"
                    >
                      {value === 1000 ? "1.0k" : value}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={playRound}
                  disabled={placing}
                  className="mt-5 rounded-[18px] bg-[#2ae278] px-6 py-5 text-[21px] font-black text-[#07110d] transition hover:bg-[#47ea89] disabled:bg-[#4a4f51] disabled:text-slate-300"
                >
                  {placing ? "Launching..." : "Bet"}
                </button>

                <div className="mt-8 rounded-[22px] bg-[#2a2f31] p-5">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                    Last round
                  </div>
                  {lastRound ? (
                    <>
                      <div className="mt-3 text-xl font-black text-white">
                        {lastRound.title}
                      </div>
                      <div className="mt-2 text-sm leading-7 text-slate-400">
                        {lastRound.detail}
                      </div>
                      <div className="mt-4 text-sm font-bold text-emerald-300">
                        Payout {formatCurrency(lastRound.payout)}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 text-sm leading-7 text-slate-500">
                      Place a round and the result will land here.
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/casino")}
                  className="mt-4 rounded-[16px] border border-white/8 px-4 py-3 text-sm font-bold text-slate-300 transition hover:bg-white/5"
                >
                  Back to lobby
                </button>
              </div>
            </aside>

            <section className="order-1 flex min-w-0 flex-col bg-[#3b4042] lg:order-2">
              <div className="px-4 pb-4 pt-3 sm:px-6">
                <div className="rounded-[18px] bg-[#353a3d] px-4 py-3 text-center text-[17px] font-black text-slate-300 sm:text-[18px]">
                  Game result will be displayed
                </div>
              </div>

              <div className="relative flex-1 overflow-hidden px-4 pb-4 sm:px-6">
                <div className="relative flex h-full min-h-[620px] flex-col overflow-hidden rounded-[24px] bg-[linear-gradient(180deg,#444a4c_0%,#505557_42%,#3f4447_100%)]">
                  <MoonLandscape />

                  <div className="relative z-10 flex flex-1 flex-col justify-between">
                    <div className="pt-10 text-center">
                      <div className="text-[74px] font-black leading-none text-[#2ae278] sm:text-[96px]">
                        {currentValue.toFixed(2)}x
                      </div>
                    </div>

                    <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4">
                      <div
                        className="relative transition-transform duration-150"
                        style={{
                          transform: `translateY(${Math.round(rocketLift * -130)}px)`,
                        }}
                      >
                        <div className="absolute left-1/2 top-[185px] h-24 w-7 -translate-x-1/2 rounded-full bg-[radial-gradient(circle,#ffd166_0%,#ff7a00_45%,rgba(255,122,0,0)_80%)] blur-[2px]" />
                        <div className="absolute left-[53px] top-[208px] h-14 w-6 rotate-[10deg] rounded-full bg-[radial-gradient(circle,#ff9640_0%,rgba(255,122,0,0)_75%)] blur-[1px]" />
                        <div className="absolute right-[53px] top-[208px] h-14 w-6 -rotate-[10deg] rounded-full bg-[radial-gradient(circle,#ff9640_0%,rgba(255,122,0,0)_75%)] blur-[1px]" />

                        <div className="relative flex h-[250px] w-[150px] items-start justify-center">
                          <div className="absolute left-1/2 top-[28px] h-[168px] w-[86px] -translate-x-1/2 rounded-[44px] bg-[linear-gradient(180deg,#ffffff_0%,#edf2f7_42%,#d2dae5_100%)] shadow-[0_25px_50px_rgba(0,0,0,0.25)]" />
                          <div className="absolute left-1/2 top-0 h-[56px] w-[58px] -translate-x-1/2 rounded-t-[40px] rounded-b-[16px] bg-[linear-gradient(180deg,#81ef18_0%,#5ccf12_100%)]" />
                          <div className="absolute left-[7px] top-[102px] h-[88px] w-[34px] -rotate-[26deg] rounded-[26px_12px_18px_18px] bg-[linear-gradient(180deg,#7de11a_0%,#5fc50d_100%)]" />
                          <div className="absolute right-[7px] top-[102px] h-[88px] w-[34px] rotate-[26deg] rounded-[12px_26px_18px_18px] bg-[linear-gradient(180deg,#7de11a_0%,#5fc50d_100%)]" />
                          <div className="absolute left-1/2 top-[78px] h-[46px] w-[46px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_30%_30%,#43484c_0%,#1f2226_68%,#111315_100%)] shadow-[inset_0_0_0_6px_rgba(188,199,214,0.55)]" />
                          <div className="absolute left-1/2 top-[128px] h-[62px] w-[14px] -translate-x-1/2 rounded-full bg-[linear-gradient(180deg,#8ef524_0%,#53bf0d_100%)]" />
                          <div className="absolute left-1/2 top-[112px] -translate-x-1/2 text-[12px] font-black uppercase tracking-[0.2em] text-[#ff8800]">
                            {game.name}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 px-4 pb-5 sm:px-6">
                      <div className="rounded-[22px] bg-[#3a3f42] p-3 shadow-[0_14px_40px_rgba(0,0,0,0.2)]">
                        <div className="grid gap-3 md:grid-cols-[1fr_180px_1fr]">
                          <div className="rounded-[18px] bg-[#313538] px-4 py-3">
                            <div className="text-[13px] font-black text-slate-300">
                              Payout
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                              <input
                                type="number"
                                min="1.1"
                                max="25"
                                step="0.01"
                                value={state.target}
                                onChange={(event) =>
                                  setState((current) => ({
                                    ...current,
                                    target: clamp(
                                      Number(event.target.value || 1.98),
                                      1.1,
                                      25,
                                    ).toFixed(2),
                                  }))
                                }
                                className="w-full rounded-[14px] bg-[#25292b] px-4 py-4 text-[18px] font-black text-white outline-none"
                              />
                              <span className="text-[24px] font-black text-white">
                                ×
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 self-end">
                            <button
                              type="button"
                              onClick={() =>
                                setState((current) => ({
                                  ...current,
                                  target: changeLimboTarget(current.target, -0.01),
                                }))
                              }
                              className="rounded-[16px] bg-[#44484b] px-4 py-4 text-3xl font-black text-white transition hover:bg-[#50565a]"
                            >
                              ‹
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setState((current) => ({
                                  ...current,
                                  target: changeLimboTarget(current.target, 0.01),
                                }))
                              }
                              className="rounded-[16px] bg-[#44484b] px-4 py-4 text-3xl font-black text-white transition hover:bg-[#50565a]"
                            >
                              ›
                            </button>
                          </div>

                          <div className="rounded-[18px] bg-[#313538] px-4 py-3">
                            <div className="text-[13px] font-black text-slate-300">
                              Win Chance
                            </div>
                            <div className="mt-3 flex items-center justify-between rounded-[14px] bg-[#25292b] px-4 py-4">
                              <div className="text-[18px] font-black text-white">
                                {winChance.toFixed(2)}
                              </div>
                              <div className="text-[24px] font-black text-white">
                                %
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-slate-300">
                        <div className="flex items-center gap-8 text-[15px] font-bold">
                          <span className="flex items-center gap-2">
                            <span className="text-2xl text-slate-400">⬢</span>
                            {formatCurrency(balance)}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-2xl text-slate-400">★</span>
                            6715
                          </span>
                        </div>
                        <div className="flex items-center gap-8 text-[22px] text-slate-400">
                          <span>⚡</span>
                          <span>∿</span>
                          <span className="rounded-[14px] bg-[#2ae278] px-3 py-1 text-[#08110c]">
                            ◔
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function MoonLandscape() {
  return (
    <>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%)] opacity-60" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.06),transparent_40%)]" />
      <div className="absolute left-[8%] top-[20%] h-2 w-2 rounded-full bg-white/40 shadow-[180px_40px_0_rgba(255,255,255,0.15),360px_15px_0_rgba(255,255,255,0.18),540px_95px_0_rgba(255,255,255,0.18),760px_36px_0_rgba(255,255,255,0.16),910px_120px_0_rgba(255,255,255,0.14)]" />
      <div className="absolute left-[12%] top-[26%] h-20 w-1 rotate-[-42deg] rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.7),rgba(255,255,255,0.02))] blur-[1px]" />
      <div className="absolute left-[26%] top-[34%] h-28 w-1 rotate-[-28deg] rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.45),rgba(255,255,255,0.02))] blur-[1px]" />
      <div className="absolute right-[10%] top-[28%] h-24 w-1 rotate-[38deg] rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.45),rgba(255,255,255,0.02))] blur-[1px]" />
      <div className="absolute right-[8%] top-[32%] h-[140px] w-[140px] rounded-full bg-white/6 blur-[2px]" />
      <div className="absolute right-[4%] top-[30%] h-[94px] w-[160px] rotate-[18deg] rounded-full border-[6px] border-white/10" />
      <div className="absolute bottom-[18%] left-0 h-[33%] w-[38%] bg-[#70777b] [clip-path:polygon(0_100%,0_44%,8%_61%,15%_37%,23%_53%,31%_24%,45%_42%,58%_21%,74%_52%,88%_36%,100%_63%,100%_100%)] opacity-65" />
      <div className="absolute bottom-[17%] left-[22%] h-[22%] w-[26%] bg-[#60686d] [clip-path:polygon(0_100%,0_68%,12%_54%,25%_69%,36%_44%,45%_58%,57%_34%,68%_53%,82%_27%,100%_59%,100%_100%)] opacity-80" />
      <div className="absolute bottom-[14%] right-0 h-[32%] w-[38%] bg-[#626a6f] [clip-path:polygon(0_100%,0_62%,11%_47%,18%_61%,32%_38%,41%_53%,56%_29%,70%_48%,84%_30%,100%_58%,100%_100%)] opacity-70" />
      <div className="absolute bottom-[14%] left-0 right-0 h-[16%] bg-[#6c7275]" />
      <div className="absolute bottom-[8%] left-[48%] h-10 w-20 -translate-x-1/2 rounded-full bg-white/60 blur-[1px]" />
      <div className="absolute bottom-[7%] left-[45.5%] h-12 w-12 rounded-full bg-[radial-gradient(circle,#edf2ff_0%,rgba(237,242,255,0.55)_60%,rgba(237,242,255,0)_100%)] opacity-80" />
      <div className="absolute bottom-[7.5%] left-[49.2%] h-14 w-14 rounded-full bg-[radial-gradient(circle,#dfe7ff_0%,rgba(223,231,255,0.45)_60%,rgba(223,231,255,0)_100%)] opacity-80" />
    </>
  );
}

function GameStage({ game, state, setState, lastRound, visualState }) {
  if (game.slug === "limbo") {
    const target = Number(state.target || 1.98);
    const currentValue = visualState.limboAnimating
      ? visualState.limboValue
      : Number(lastRound?.meta?.crashAt || 1);
    const progress = clamp(currentValue / 10, 0.08, 1);

    return (
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top,rgba(243,115,24,0.12),transparent_22%),linear-gradient(180deg,#1b1f27,#11141a)] p-4 sm:p-6">
        <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[24px] bg-[#20252e] p-4">
            <div className="flex items-center justify-between gap-3 rounded-[18px] bg-[#181d25] px-4 py-3">
              <div className="text-sm font-semibold text-slate-300">
                Game results will be displayed here.
              </div>
              <div className="rounded-full bg-[#232a35] px-3 py-1 text-xs font-bold text-emerald-300">
                Bankroll BTC 612.865101
              </div>
            </div>
            <div className="relative mt-6 flex min-h-[370px] items-center justify-center overflow-hidden rounded-[24px] bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05),transparent_55%),linear-gradient(180deg,#1b1f27,#12151c)]">
              <div className="absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.03))]" />
              <div className="absolute bottom-12 left-10 right-10 h-1 rounded-full bg-[#303641]" />
              <div
                className="absolute bottom-[62px] left-[12%] transition-all duration-150"
                style={{ transform: `translateX(${progress * 320}px)` }}
              >
                <div className="relative">
                  <div className="absolute left-1/2 top-[70px] h-16 w-1 -translate-x-1/2 bg-gradient-to-b from-orange-300 via-orange-500 to-transparent blur-[1px]" />
                  <div className="relative flex h-28 w-20 items-center justify-center rounded-[40%_40%_48%_48%] bg-[linear-gradient(180deg,#ffffff,#cbd5e1)] shadow-[0_20px_40px_rgba(0,0,0,0.35)]">
                    <div className="absolute inset-y-3 left-2 w-3 rounded-full bg-lime-400" />
                    <div className="absolute inset-y-3 right-2 w-3 rounded-full bg-lime-400" />
                    <div className="h-7 w-7 rounded-full bg-[#303641]" />
                  </div>
                </div>
              </div>
              <div className="absolute top-10 text-center">
                <div className="text-7xl font-black text-[#ff7a00]">
                  {currentValue.toFixed(2)}x
                </div>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <SurfaceStat label="Payout" value={target.toFixed(2)} />
              <SurfaceStat
                label="Win Chance"
                value={`${(100 / Math.max(target, 1.01)).toFixed(4)}%`}
                valueClassName="text-emerald-300"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (game.slug === "dice-duel") {
    const line = Number(state.line || 50);
    const roll = clamp(Number(visualState.diceValue || 50), 0, 99);
    const winChance =
      (state.direction === "over" ? 100 - line : line) / 100;
    const payout = lastRound?.meta?.payoutMultiplier
      ? lastRound.meta.payoutMultiplier.toFixed(2)
      : (0.97 / Math.max(winChance, 0.05)).toFixed(2);

    return (
      <div className="overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#171b22,#11151c)] p-4 sm:p-6">
        <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[24px] bg-[#20252e] p-4">
            <div className="flex items-center justify-between gap-3 rounded-[18px] bg-[#181d25] px-4 py-3">
              <div className="text-sm font-semibold text-slate-300">
                Slider result board
              </div>
              <div className="rounded-full bg-[#232a35] px-3 py-1 text-xs font-bold text-emerald-300">
                {state.direction === "over" ? "Roll Over" : "Roll Under"}
              </div>
            </div>
            <div className="mt-6 rounded-[24px] bg-[#14181f] px-4 py-10">
              <div className="flex justify-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-[26px] bg-white text-3xl font-black text-[#171b22] shadow-[0_15px_35px_rgba(255,255,255,0.12)]">
                  {roll.toFixed(2)}
                </div>
              </div>
              <div className="relative mt-12">
                <div className="h-5 rounded-full bg-[linear-gradient(90deg,#5ac943_0%,#5ac943_50%,#f97316_50%,#f97316_100%)]" />
                <div
                  className="absolute top-1/2 h-9 w-3 -translate-y-1/2 rounded-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.35)]"
                  style={{ left: `calc(${line}% - 6px)` }}
                />
              </div>
              <div className="mt-5 flex justify-between px-1 text-xs text-slate-500">
                {[0, 20, 40, 60, 80, 100].map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
              <div className="mt-6 grid gap-3 md:grid-cols-3">
                <SurfaceStat label="Target" value={String(line)} />
                <SurfaceStat label="Payout" value={`${payout}x`} valueClassName="text-emerald-300" />
                <SurfaceStat
                  label="Win Chance"
                  value={`${(((state.direction === "over" ? 100 - line : line) / 100) * 100).toFixed(2)}%`}
                  valueClassName="text-amber-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (game.slug === "coin-toss") {
    const coinFace = visualState.coinFace;
    const multiplier = lastRound?.payout ? (lastRound.payout > 0 ? "1.96x" : "0.00x") : "x0.00";

    return (
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top,rgba(250,204,21,0.12),transparent_25%),linear-gradient(180deg,#171b22,#101318)] p-4 sm:p-6">
        <div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(24,30,37,0.96),rgba(18,22,29,0.96))] p-4">
          <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr_0.95fr]">
            <SurfaceStat label="Series" value="0" />
            <div className="flex min-h-[340px] items-center justify-center rounded-[24px] bg-[radial-gradient(circle_at_center,rgba(255,207,88,0.12),transparent_45%),linear-gradient(180deg,#1b1f27,#151922)]">
              <div
                className={`flex h-44 w-44 items-center justify-center rounded-full border-[8px] border-[#f7b733] bg-[radial-gradient(circle_at_35%_35%,#ffe8a3,#fbbf24_60%,#d97706)] text-3xl font-black text-[#442100] shadow-[0_20px_50px_rgba(245,158,11,0.28)] ${
                  visualState.coinFlipping ? "animate-spin" : ""
                }`}
              >
                {coinFace === "heads" ? "H" : "T"}
              </div>
            </div>
            <SurfaceStat label="Multiply" value={multiplier} valueClassName="text-emerald-300" />
          </div>
        </div>
      </div>
    );
  }

  if (game.slug === "lucky-wheel") {
    const risk = state.risk || "medium";
    const segments = luckyWheelSegments[risk];
    const conic = segments
      .map((segment, index) => {
        const start = (360 / segments.length) * index;
        const end = (360 / segments.length) * (index + 1);
        return `${segment.color} ${start}deg ${end}deg`;
      })
      .join(", ");

    return (
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top,rgba(244,114,182,0.14),transparent_24%),linear-gradient(180deg,#191722,#11131a)] p-4 sm:p-6">
        <div className="flex min-h-[560px] items-center justify-center rounded-[24px] bg-[linear-gradient(180deg,#201d29,#161922)] p-4">
          <div className="relative flex flex-col items-center">
            <div className="absolute top-2 z-20 h-0 w-0 border-l-[16px] border-r-[16px] border-t-[24px] border-l-transparent border-r-transparent border-t-[#ffb55f]" />
            <div
              className="relative flex h-[320px] w-[320px] items-center justify-center rounded-full border-[10px] border-[#f7b36a] shadow-[0_25px_70px_rgba(0,0,0,0.35)] transition-transform duration-[2200ms] ease-out"
              style={{
                transform: `rotate(${visualState.wheelRotation}deg)`,
                background: `conic-gradient(${conic})`,
              }}
            >
              <div className="absolute inset-3 rounded-full border border-white/10" />
              {segments.map((segment, index) => {
                const angle = (360 / segments.length) * index + 22.5;
                return (
                  <div
                    key={`${segment.label}-${index}`}
                    className="absolute left-1/2 top-1/2 text-xs font-black text-white"
                    style={{
                      transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-120px)`,
                    }}
                  >
                    {segment.label}
                  </div>
                );
              })}
              <div className="relative z-10 flex h-28 w-28 items-center justify-center rounded-full bg-[#7b2cbf] text-center text-xl font-black text-[#ffd166] shadow-[0_10px_30px_rgba(123,44,191,0.4)]">
                Lucky
                <br />
                Spin
              </div>
            </div>
            <div className="mt-8 rounded-[20px] bg-[#f28c52] px-8 py-4 text-base font-black text-white shadow-[0_14px_35px_rgba(242,140,82,0.25)]">
              Lucky Spin: 1
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (game.slug === "keno") {
    const picks = state.picks || [];
    const reveal = visualState.kenoReveal || [];
    const hitCount = picks.filter((value) => reveal.includes(value)).length;

    return (
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top,rgba(139,92,246,0.16),transparent_26%),linear-gradient(180deg,#171722,#10131a)] p-4 sm:p-6">
        <div className="rounded-[24px] bg-[#151923] p-4">
          <div className="grid gap-3 md:grid-cols-5">
            {["1.80x", "0.00x", "1.20x", "1.20x", "1.30x"].map((value) => (
              <div
                key={value}
                className={`rounded-[14px] px-3 py-3 text-center text-sm font-black ${
                  value === "0.00x" ? "bg-[#2a3039] text-slate-500" : "bg-[#31c96a] text-[#07110d]"
                }`}
              >
                {value}
              </div>
            ))}
          </div>
          <div className="mt-6 grid gap-2 sm:grid-cols-5 lg:grid-cols-10">
            {Array.from({ length: 40 }, (_, index) => index + 1).map((value) => {
              const selected = picks.includes(value);
              const revealed = reveal.includes(value);
              const hit = selected && revealed;

              return (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setState((current) => {
                      const exists = current.picks.includes(value);
                      if (exists) {
                        return {
                          ...current,
                          picks: current.picks.filter((entry) => entry !== value),
                        };
                      }
                      if (current.picks.length >= 10) return current;
                      return {
                        ...current,
                        picks: [...current.picks, value],
                      };
                    })
                  }
                  className={`flex h-14 items-center justify-center rounded-[14px] text-sm font-black transition ${
                    hit
                      ? "bg-[#7c3aed] text-white"
                      : revealed
                        ? "bg-[#31c96a] text-[#07110d]"
                        : selected
                          ? "bg-[#6d28d9] text-white"
                          : "bg-[#20242d] text-slate-500 hover:bg-[#262c36]"
                  }`}
                >
                  {value}
                </button>
              );
            })}
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-5 lg:grid-cols-10">
            {kenoPayouts.map((value, index) => (
              <div
                key={`${value}-${index}`}
                className="rounded-[12px] bg-[#232a35] px-2 py-2 text-center text-[11px] font-semibold text-slate-400"
              >
                <div>{value.toFixed(2)}x</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.18em]">
                  {index} hits
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-[18px] bg-[#181d25] px-4 py-3 text-sm font-semibold text-slate-300">
            Hits: <span className="text-violet-300">{hitCount}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#171b22,#10141a)] p-6">
      <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
        Game Stage
      </div>
      <div className="mt-4 text-3xl font-black text-white">{game.name}</div>
      <p className="mt-4 max-w-2xl text-sm leading-8 text-slate-300">
        {lastRound
          ? lastRound.detail
          : "Play a round to populate this game stage with live feedback and results."}
      </p>
      {lastRound?.chips?.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {lastRound.chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full bg-[#232a35] px-3 py-2 text-xs font-semibold text-slate-200"
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
